import React, { useCallback, useEffect, useState } from 'react';
import {
  CircularProgress,
  Dialog,
  IconButton,
  makeStyles,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { FORM_COLORS } from '../PublicationFormModal/constants';
import { fetchPublicationLogs, LogOperation, PublicationLog } from '../../api/publicationLogs';

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 14,
    width: 960,
    maxWidth: '96vw',
  },
  body: {
    padding: '26px 30px 30px',
    fontFamily: 'inherit',
    color: FORM_COLORS.text,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  title: {
    fontWeight: 600,
    fontSize: 16,
    color: FORM_COLORS.text,
  },
  publicationId: {
    fontSize: 12,
    color: FORM_COLORS.textSecondary,
    fontFamily: 'monospace',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  select: {
    fontSize: 13,
    minWidth: 160,
    '& .MuiSelect-root': {
      padding: '6px 10px',
    },
  },
  tableContainer: {
    maxHeight: 420,
    border: `1px solid ${FORM_COLORS.border}`,
    borderRadius: 8,
    overflow: 'auto',
  },
  tableHead: {
    background: '#f7f7f9',
    '& th': {
      fontWeight: 600,
      fontSize: 12,
      color: FORM_COLORS.textSecondary,
      padding: '8px 12px',
      borderBottom: `1px solid ${FORM_COLORS.border}`,
      whiteSpace: 'nowrap',
    },
  },
  tableRow: {
    '&:last-child td': {
      borderBottom: 'none',
    },
    '& td': {
      fontSize: 12,
      padding: '7px 12px',
      borderBottom: `1px solid ${FORM_COLORS.border}`,
      verticalAlign: 'top',
    },
  },
  operationBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  configValues: {
    maxWidth: 260,
    fontSize: 11,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    color: '#555',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 0',
    color: FORM_COLORS.textSecondary,
    fontSize: 13,
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px 0',
  },
}));

const OPERATION_OPTIONS: { label: string; value: '' | LogOperation }[] = [
  { label: 'Operation', value: '' },
  { label: 'Insert', value: 'insert' },
  { label: 'Update', value: 'update' },
  { label: 'Delete', value: 'delete' },
];

const OPERATION_COLORS: Record<string, { bg: string; color: string }> = {
  insert: { bg: '#e6f4ea', color: '#2e7d32' },
  update: { bg: '#fff3e0', color: '#e65100' },
  delete: { bg: '#fce4ec', color: '#c62828' },
};

const formatDate = (iso?: string) => {
  if (!iso || iso === 'N/A') return 'N/A';
  try {
    return new Date(iso).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const getLogValue = (value?: string | number | string[] | null) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (Array.isArray(value)) return value.length ? String(value[0]) : 'N/A';
  return String(value);
};

const getConfigValues = (value?: Record<string, unknown> | Record<string, unknown>[] | null) => {
  if (value === null || value === undefined) return 'N/A';
  const config = Array.isArray(value) ? value[0] : value;
  if (!config || typeof config !== 'object') return 'N/A';
  return JSON.stringify(config, null, 2);
};

interface PublicationLogsModalProps {
  open: boolean;
  publicationId: string | null;
  onClose: () => void;
}

export const PublicationLogsModal = ({ open, publicationId, onClose }: PublicationLogsModalProps) => {
  const classes = useStyles();
  const [operation, setOperation] = useState<'' | LogOperation>('');
  const [logs, setLogs] = useState<PublicationLog[]>([]);
  const [loading, setLoading] = useState(false);

  const loadLogs = useCallback(async () => {
    if (!publicationId) return;
    setLoading(true);
    try {
      const result = await fetchPublicationLogs(publicationId, operation || undefined);
      setLogs(result);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [publicationId, operation]);

  useEffect(() => {
    if (open && publicationId) {
      loadLogs();
    } else {
      setLogs([]);
      setOperation('');
    }
  }, [open, publicationId, loadLogs]);

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }} maxWidth={false}>
      <div className={classes.body}>
        <div className={classes.header}>
          <div className={classes.titleBlock}>
            <span className={classes.title}>Historial</span>
            {publicationId && (
              <span className={classes.publicationId}>{publicationId}</span>
            )}
          </div>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className={classes.toolbar}>
          <Select
            variant="outlined"
            className={classes.select}
            value={operation}
            onChange={(e) => setOperation(e.target.value as '' | LogOperation)}
          >
            {OPERATION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </div>

        {loading ? (
          <div className={classes.loadingWrapper}>
            <CircularProgress size={32} />
          </div>
        ) : (
          <TableContainer className={classes.tableContainer}>
            <Table size="small" stickyHeader>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Operación</TableCell>
                  <TableCell>Publication ID</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Config Values</TableCell>
                  <TableCell align="right">Relevance</TableCell>
                  <TableCell>Hecho Por</TableCell>
                  <TableCell>Último Cambio</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography className={classes.emptyState}>
                        No se encontraron registros.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log, index) => {
                    const op = getLogValue(log.operation).toLowerCase();
                    const opColors = OPERATION_COLORS[op] ?? { bg: '#f0f0f0', color: '#555' };

                    return (
                      <TableRow key={`${getLogValue(log.publication_log_id)}-${index}`} className={classes.tableRow}>
                        <TableCell>
                          <span
                            className={classes.operationBadge}
                            style={{ background: opColors.bg, color: opColors.color }}
                          >
                            {getLogValue(log.operation)}
                          </span>
                        </TableCell>
                        <TableCell style={{ fontFamily: 'monospace', fontSize: 11 }}>
                          {getLogValue(log.publication_id)}
                        </TableCell>
                        <TableCell>{getLogValue(log.publication_state_id)}</TableCell>
                        <TableCell>
                          <div className={classes.configValues}>
                            {getConfigValues(log.config_values)}
                          </div>
                        </TableCell>
                        <TableCell align="right">{getLogValue(log.relevance)}</TableCell>
                        <TableCell>{getLogValue(log.updated_by)}</TableCell>
                        <TableCell>{formatDate(getLogValue(log.last_change))}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    </Dialog>
  );
};
